import { useState, useCallback } from 'react';
import { FolderOpen, Plus, Command, Search } from 'lucide-react';
import { Button } from '@phrasewright/ui';
import { useAppDispatch } from '@/store/hooks';
import { toggleCommandPalette } from '@/store/slices/uiSlice';
import {
  setCurrentProject,
  addRecentProject,
} from '@/store/slices/projectSlice';
import { CreateFileDialog } from '../CreateFileDialog';
import { openFolderAndCreateProject } from '@/utils/projectUtils';
import { toast } from 'sonner';

export function WelcomeView() {
  const dispatch = useAppDispatch();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const handleOpenProject = useCallback(async () => {
    try {
      const result = await openFolderAndCreateProject();
      if (result.success && result.project) {
        dispatch(setCurrentProject(result.project));
        dispatch(addRecentProject(result.project));
        toast.success(`Opened project: ${result.project.name}`);
      } else if (result.error && result.error !== 'User cancelled') {
        toast.error(`Failed to open project: ${result.error}`);
      }
    } catch (error) {
      toast.error('Failed to open project');
    }
  }, [dispatch]);

  return (
    <div className="flex h-full items-center justify-center">
      <div className="max-w-2xl text-center">
        <h1 className="mb-4 text-4xl font-bold">Welcome to Phrasewright</h1>
        <p className="mb-8 text-lg text-muted-foreground">
          A modern editor for managing reusable text and code blocks with
          powerful @ reference syntax
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Button
            variant="outline"
            className="h-auto flex-col gap-2 p-6"
            onClick={handleOpenProject}
          >
            <FolderOpen className="h-8 w-8" />
            <div>
              <div className="font-semibold">Open Project</div>
              <div className="text-sm text-muted-foreground">
                Open an existing project folder
              </div>
            </div>
          </Button>

          <Button
            variant="outline"
            className="h-auto flex-col gap-2 p-6"
            onClick={() => setShowCreateDialog(true)}
          >
            <Plus className="h-8 w-8" />
            <div>
              <div className="font-semibold">New File</div>
              <div className="text-sm text-muted-foreground">
                Create a new document or phrase
              </div>
            </div>
          </Button>

          <Button variant="outline" className="h-auto flex-col gap-2 p-6">
            <Search className="h-8 w-8" />
            <div>
              <div className="font-semibold">Search Files</div>
              <div className="text-sm text-muted-foreground">
                Find content across all files
              </div>
            </div>
          </Button>

          <Button
            variant="outline"
            className="h-auto flex-col gap-2 p-6"
            onClick={() => dispatch(toggleCommandPalette())}
          >
            <Command className="h-8 w-8" />
            <div>
              <div className="font-semibold">Command Palette</div>
              <div className="text-sm text-muted-foreground">
                Quick access to all commands
              </div>
            </div>
          </Button>
        </div>

        <div className="mt-8 space-y-2 text-sm text-muted-foreground">
          <p>Quick Tips:</p>
          <ul className="space-y-1">
            <li>• Use @ to reference other files inline</li>
            <li>• Press ⌘K to open the command palette</li>
            <li>• Drag and drop files to import them</li>
            <li>• Use YAML frontmatter for metadata</li>
          </ul>
        </div>
      </div>

      <CreateFileDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </div>
  );
}
